import { useState } from 'react';
import { Button, Grid, makeStyles, Icon, InputAdornment, TextField, Typography, Collapse, IconButton, Hidden, Box, Divider } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { Email as EmailIcon, Lock as PasswordIcon, Close as CloseIcon } from '@material-ui/icons';
import { useMsal } from "@azure/msal-react";
import { getQueryParam } from '../../utils';
import { apiService } from '../../services';
import { useForm } from '../../hooks';
import { loginRequest } from '../../config';
import logoImage from '../../images/brand/logo.png';
import microsoftImage from '../../images/external/microsoft_icon.svg';

const useStyles = makeStyles((theme) => ({
  alert: {
    width: '100%'
  },
  brandingBottom: {
    background: `linear-gradient(to right, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.main} 50%, ${theme.palette.secondary.dark} 50%, ${theme.palette.secondary.dark}) 100%;`,
    height: `${theme.spacing(3)}px`,
  },
  brandingLogo: {
    width: '100%',
    maxWidth: '180px',
    marginBottom: `${theme.spacing(1)}px`,
  },
  brandingTop: {
    background: `linear-gradient(to right, ${theme.palette.primary.main} 0%, ${theme.palette.primary.main} 50%, ${theme.palette.primary.dark} 50%, ${theme.palette.primary.dark});`,
    height: `${theme.spacing(3)}px`,
  },
  form: {
    flexGrow: 1,
    padding: `${theme.spacing(4)}px ${theme.spacing(4)}px`,
  },
  formHeader: {
    color: `${theme.palette.text.hint}`,
    fontFamily: "'Raleway', sans-serif",
    fontWeight: '500',
    fontSize: '20px',
    letterSpacing: '0.25em'
  },
  inputAdornment: {
    color: '#707487'
  },
  icon: {
    textAlign: 'center',
  },
  iconImage: {
    display: 'flex',
    height: 'inherit',
    width: 'inherit'
  }
}));

export default function LoginForm(props) {
  const classes = useStyles();

  const [alertMessage, setAlertMessage] = useState(null);
  const [loading, setLoading] = useState(false);

  const { instance } = useMsal();
  const { register, submit, errors } = useForm();

  async function handleMicrosoftLogin() {
    window.location.replace("/api/external/challenge");
  };

  async function handleLogin(loginRequest) {
    loginRequest["returnUrl"] = getQueryParam("ReturnUrl");

    const response = await apiService.account.login(loginRequest);

    if (response.ok) {
      const data = response.data;

      if (!data.hasError) {
        setAlertMessage(null);
        setLoading(true);
        window.location.replace(data.redirectUrl);
      } else {
        setAlertMessage(data.message);
      }

    } else {
      setAlertMessage(response.error);
    }
  };

  return (
    <Grid
      className={classes.formContainer}
      container
    >
      <Grid
        item
        className={classes.brandingTop}
        xs={12}
      />
      <Grid
        item
        xs={12}
      >
        <form
          className={classes.form}
          onSubmit={submit(handleLogin)}
          noValidate
        >
          <Grid
            container
            spacing={2}
            justify="center"
          >
            <Hidden mdUp>
              <Grid item xs={12}>
                <Box
                  display="flex"
                  justifyContent="center"
                >
                  <img
                    className={classes.brandingLogo}
                    alt="iCompare Logo"
                    src={logoImage}
                  />
                </Box>
              </Grid>
            </Hidden>
            <Grid item xs={12}>
              <Typography
                className={classes.formHeader}
                variant='h4'
              >
                LOGIN
                  </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                {...register("email", { required: true, pattern: /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/ })}
                variant="filled"
                label="Email"
                type="email"
                autoComplete="email"
                autoFocus
                size="small"
                required
                fullWidth
                InputProps={{
                  className: classes.input,
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon className={classes.inputAdornment} fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                error={!!errors.email}
                helperText={errors.email?.message}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                {...register("password", { required: true })}
                variant="filled"
                label="Password"
                type="password"
                autoComplete="current-password"
                size="small"
                required
                fullWidth
                InputProps={{
                  className: classes.input,
                  startAdornment: (
                    <InputAdornment position="start">
                      <PasswordIcon className={classes.inputAdornment} fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                error={!!errors.password}
                helperText={errors.password?.message}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                className={classes.signInButton}
                fullWidth
                type="submit"
                variant="contained"
                color="secondary"
                disabled={loading}
              >
                {loading ? "Signing in ..." : "Sign In"}
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid item xs={12}>
              <Button
                fullWidth
                variant="outlined"
                color="primary"
                startIcon={<Icon className={classes.icon}><img className={classes.iconImage} src={microsoftImage} /></Icon>}
                onClick={handleMicrosoftLogin}
              >
                Microsoft
              </Button>
            </Grid>
            <Collapse in={alertMessage ? true : false}>
              <Grid
                item
                xs={12}
              >
                <Alert
                  action={
                    <IconButton
                      color="inherit"
                      size="small"
                      onClick={() => setAlertMessage(null)}
                    >
                      <CloseIcon fontSize="inherit" />
                    </IconButton>
                  }
                  className={classes.alert}
                  severity="error"
                >
                  {alertMessage}
                </Alert>
              </Grid>
            </Collapse>
          </Grid>
        </form>
      </Grid>
      <Grid
        className={classes.brandingBottom}
        item
        xs={12}
      />
    </Grid>
  );
};